import React, { useMemo, useState } from 'react';
import { format, startOfDay, endOfDay } from 'date-fns';
import { useGatheringsForDateRange } from '../../gatherings/hooks/data/useGatheringsForDateRange';
import { GatheringStatus } from '../../../types';
import GatheringInfoModal from './GatheringInfoModal';

interface DailyGatheringsOverviewProps {
  token: string | null;
  selectedSpaceId?: string;
  className?: string;
}

const DailyGatheringsOverview: React.FC<DailyGatheringsOverviewProps> = ({
  token,
  selectedSpaceId,
  className = ''
}) => {
  // Get today's date range (start and end of day)
  const today = new Date();
  const startOfToday = startOfDay(today);
  const endOfToday = endOfDay(today);
  
  // Fetch today's gatherings using existing hook
  const { 
    gatherings: allTodayGatherings, 
    isLoading, 
    error, 
    refresh 
  } = useGatheringsForDateRange(startOfToday, endOfToday, token);
  
  // State for gathering info modal
  const [selectedGatheringId, setSelectedGatheringId] = useState<string | null>(null);

  // Filter gatherings: only published + space filter if selected
  const filteredGatherings = useMemo(() => {
    return allTodayGatherings.filter(gathering => {
      // Only show published gatherings
      if (gathering.status !== GatheringStatus.PUBLISHED) {
        return false;
      }
      
      // Apply space filter if selected
      if (selectedSpaceId && gathering.space?.id !== selectedSpaceId) {
        return false;
      }
      
      return true;
    });
  }, [allTodayGatherings, selectedSpaceId]);

  // Group gatherings by space name
  const gatheringsBySpace = useMemo(() => {
    const grouped: Record<string, typeof filteredGatherings> = {};
    
    filteredGatherings.forEach(gathering => {
      const spaceName = gathering.space?.name || 'Unknown Location';
      if (!grouped[spaceName]) {
        grouped[spaceName] = [];
      }
      grouped[spaceName].push(gathering);
    });
    
    // Sort gatherings within each space by start time
    Object.keys(grouped).forEach(spaceName => {
      grouped[spaceName].sort((a, b) => {
        return a.start_time.localeCompare(b.start_time);
      });
    });
    
    return grouped;
  }, [filteredGatherings]);

  // Format time for display (convert 24h to 12h format)
  const formatTime = (timeString: string): string => {
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };
  
  // Handle gathering click to open modal
  const handleGatheringClick = (gatheringId: string) => {
    setSelectedGatheringId(gatheringId);
  };
  
  // Handle modal close
  const handleModalClose = () => {
    setSelectedGatheringId(null);
  };

  if (isLoading) {
    return (
      <div className={`daily-gatherings-overview ${className}`}>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading today's gatherings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`daily-gatherings-overview ${className}`}>
        <div className="error-message">
          Error loading gatherings: {error}
          <button onClick={refresh} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const spaceNames = Object.keys(gatheringsBySpace);
  const totalGatherings = filteredGatherings.length;

  if (totalGatherings === 0) {
    return (
      <div className={`daily-gatherings-overview ${className}`}>
        <div className="no-gatherings">
          <p>No published gatherings scheduled for today</p>
          {selectedSpaceId && (
            <p className="filter-note">
              Try selecting "All Spaces" to see gatherings at other locations
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`daily-gatherings-overview ${className}`}>
      <div className="gatherings-summary">
        <p className="summary-text">
          {totalGatherings} gathering{totalGatherings !== 1 ? 's' : ''} today
          {spaceNames.length > 1 && ` across ${spaceNames.length} locations`}
        </p>
      </div>

      <div className="gatherings-by-location">
        {spaceNames.map(spaceName => (
          <div key={spaceName} className="location-group">
            <h3 className="location-name">{spaceName}</h3>
            <div className="gatherings-list">
              {gatheringsBySpace[spaceName].map(gathering => (
                <div 
                  key={gathering.id} 
                  className="gathering-item"
                  onClick={() => handleGatheringClick(gathering.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleGatheringClick(gathering.id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  style={{
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.03)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div className="gathering-time">
                    {formatTime(gathering.start_time)}
                    {gathering.end_time && (
                      <span className="end-time">
                        - {formatTime(gathering.end_time)}
                      </span>
                    )}
                  </div>
                  <div className="gathering-title">{gathering.title}</div>
                  {gathering.event_size !== undefined && (
                    <div className="gathering-attendees">
                      Max {gathering.event_size} attendee{gathering.event_size !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      {/* Gathering Info Modal */}
      <GatheringInfoModal
        gatheringId={selectedGatheringId}
        isOpen={selectedGatheringId !== null}
        onClose={handleModalClose}
        token={token}
      />
    </div>
  );
};

export default DailyGatheringsOverview;
